import React from 'react';
import Icon from '@/components/ui/AppIcon';
import type { BlogPost } from './ResourceContent';

type Props = {
  post: BlogPost;
  typeColors: Record<string, string>;
  expanded: boolean;
  onExpand: () => void;
};

const categoryColors: Record<string, string> = {
  'Immigration Law': 'bg-rose-100 text-rose-700',
  'Civic Rights': 'bg-blue-100 text-blue-700',
  'Advocacy Guides': 'bg-amber-100 text-amber-700',
  'Legal Aid': 'bg-emerald-100 text-emerald-700',
};

export default function FeaturedArticle({ post, typeColors, expanded, onExpand }: Props) {
  return (
    <div className="card-base overflow-hidden">
      {/* Student disclaimer banner */}
      <div className="flex items-center gap-2 px-8 py-3 bg-amber-50 border-b border-amber-200">
        <Icon name="AcademicCapIcon" size={14} variant="solid" className="text-amber-600 flex-shrink-0" />
        <span className="text-xs text-amber-800 font-medium">
          Student-written article · {post.school} — We welcome new student submissions every week.
        </span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-0">
        {/* Left: content */}
        <div className="lg:col-span-3 p-8 lg:p-10">
          <div className="flex items-center gap-3 mb-4">
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-primary/10 text-primary text-xs font-bold">
              <Icon name="StarIcon" size={11} variant="solid" className="text-primary" />
              Featured Article
            </span>
            <span className={`badge-skill ${typeColors[post.type]}`}>{post.type}</span>
            <span className={`badge-skill ${categoryColors[post.category]}`}>{post.category}</span>
          </div>
          <h2 className="text-xl lg:text-2xl font-extrabold text-foreground mb-3 leading-snug">
            {post.title}
          </h2>
          <p className="text-sm text-zinc-500 leading-relaxed mb-4 max-w-xl">
            {post.excerpt}
          </p>

          {/* Full article content */}
          {expanded && (
            <div className="mb-6 p-5 rounded-xl bg-zinc-50 border border-border">
              <div className="prose prose-sm max-w-none text-zinc-700 leading-relaxed whitespace-pre-line text-sm">
                {post.fullContent}
              </div>
            </div>
          )}

          {/* Tags */}
          <div className="flex flex-wrap gap-2 mb-6">
            {post.tags.map((tag) => (
              <span
                key={`feat-tag-${post.id}-${tag}`}
                className="px-2.5 py-1 rounded-md bg-zinc-100 text-zinc-600 text-xs font-medium"
              >
                #{tag}
              </span>
            ))}
          </div>

          {/* Author + meta */}
          <div className="flex items-center gap-4 pt-5 border-t border-border">
            <div className="w-10 h-10 rounded-full crimson-gradient flex items-center justify-center flex-shrink-0">
              <span className="text-white text-sm font-bold">{post.author[0]}</span>
            </div>
            <div>
              <p className="text-sm font-bold text-foreground">{post.author}</p>
              <p className="text-xs text-muted-foreground">{post.school}</p>
            </div>
            <div className="ml-auto flex items-center gap-4 text-xs text-muted-foreground">
              <span className="flex items-center gap-1">
                <Icon name="CalendarIcon" size={13} variant="outline" />
                {post.date}
              </span>
              <span className="flex items-center gap-1">
                <Icon name="ClockIcon" size={13} variant="outline" />
                {post.readTime}
              </span>
            </div>
          </div>

          <button onClick={onExpand} className="btn-primary mt-6 px-6" suppressHydrationWarning>
            {expanded ? 'Collapse Article' : 'Read Full Article'}
            <Icon name={expanded ? 'ChevronUpIcon' : 'ArrowRightIcon'} size={15} variant="outline" />
          </button>
        </div>

        {/* Right: visual */}
        <div className="lg:col-span-2 bg-gradient-to-br from-zinc-900 to-primary-dark p-8 flex flex-col justify-center items-center text-center min-h-[260px]">
          <div className="w-20 h-20 rounded-2xl bg-white/10 flex items-center justify-center mb-5">
            <Icon name="ScaleIcon" size={36} variant="outline" className="text-white" />
          </div>
          <p className="text-white font-bold text-lg mb-2 leading-snug">
            Immigration Law Expertise
          </p>
          <p className="text-zinc-400 text-sm leading-relaxed mb-4">
            Content reviewed by licensed immigration attorneys and updated following Supreme Court decisions.
          </p>
          <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 border border-white/20">
            <Icon name="ShieldCheckIcon" size={14} variant="solid" className="text-emerald-400" />
            <span className="text-xs text-white font-semibold">Legally Reviewed Content</span>
          </div>
        </div>
      </div>
    </div>
  );
}